/** @jsxImportSource @emotion/react */
import {GuestDetails} from '../../../guestDetails';
import ImmutableMap from '../../../../language/ImmutableMap';
import {EntitlementType} from '../../../../entitlementTypes/entitlementType';
import {Resort} from '../../../../resortDetails/resort';
import {RedemptionType} from '../redemptionType';
import {EntitlementRedemptionHistory} from './EntitlementRedemptionHistory';
import {Entitlement, QuickEntitlement} from '../../models';
import {ApiError} from '../../../../core/redux/api/apiError';
import {Redemption} from '../redemption';
import {ColumnConfig} from '../../../../layout/table/columnConfig';
import {HistoricalRedemption} from '../historicalRedemption';
import {isNilOrEmpty, sortByDateProp, sortByProp} from '../../../../language/module';
import {css} from '@emotion/react';
import {COOL_GREY, LIGHT_GREY} from '../../../../styles/colors';
import {useEffect, useState} from 'react';
import {ALL_RESORTS, getAggregateStats, getResortName} from './module';
import * as R from 'ramda';
import PassResortSelector, {ResortDropdownItem} from './PassResortSelector';
import * as React from 'react';
import {getFriendlyDays, getRedemptionType} from '../module';
import {formatDate, toTwoDigitYearRanges} from '../../../../language/time';
import xCircle from '../../../../assets/icons/xCircle.svg';
import ModalView, {ModalContent} from '../../../../layout/modal/ModalView';
import ModalTitleHeader from '../../../../layout/modal/ModalTitleHeader';
import dtw from '../../../../assets/icons/daypass.svg';
import {DayCorrectionForm} from '../DayCorrectionForm';
import StatSummary from '../../../../layout/StatSummary';
import {RedemptionDetails} from '../RedemptionDetails';
import {COLUMN_SPACING} from '../../../../forms/styles';
import LabeledData from '../../../../forms/elements/LabeledData';
import {fullName} from '../../../module';
import ErrorView from '../../../../forms/ErrorView';
import {apostrophize} from '../../../../language/strings';
import Table from '../../../../layout/table/Table';
import {combineSplitUsageDays} from '../../CombineSplitUsage';
import { getResource } from '../../../../language/resources/getResource';
import { IAppState } from '../../../../core/redux/IAppState';
import { useSelector } from 'react-redux';

const HorizontalTable = css`
  background-color: ${LIGHT_GREY};
  border: 1px solid ${COOL_GREY};
  padding: 10px;
`;

const ViewDaysStats = css`
  display: flex;
  justify-content: space-around;
  align-items: baseline;
  ${HorizontalTable};
`;

type Props = {
  guest: GuestDetails;
  passEntitlementId?: number;
  allowCorrectiveRedemption: boolean;
  allowGlobalRedemption: boolean;

  allEntitlementTypes: ImmutableMap<EntitlementType>;

  homeResort?: Resort;
  allResorts?: ImmutableMap<Resort>;

  redemptionTypes?: RedemptionType[];
  redemptions?: EntitlementRedemptionHistory[];

  passes?: QuickEntitlement[];
  preferredPass?: QuickEntitlement;
  resortHistories?: ImmutableMap<Entitlement>;

  // Loading
  isRedeeming
  isLoadingResorts?: boolean,
  isLoadingResortHistory?: (resortAbbrev: string) => boolean,

  // Error
  errorResortHistory?: ApiError,

  // Actions
  getResortHistory?: (guestId: string, entitlementId: number, resortAbbrev: string, resortName: string) => void;
  getRedemptionTypes?: () => void;
  redeemEntitlement: (redemption: Redemption) => void;
};


export const ViewDaysTab = (props: Props) => {
  const [selectedPassId, setSelectedPassId] = useState(props.passEntitlementId);
  const [selectedResort, setSelectedResort] = useState(props.homeResort?.resortAbbreviation || '');
  const [selectedRedemption, setSelectedRedemption] = useState<HistoricalRedemption | undefined>(undefined);
  const [isDayCorrectionModalOpen, setIsDayCorrectionModalOpen] = useState(false);
  const [dayCorrectionNotes, setDayCorrectionNotes] = useState('');
  const resources = useSelector((state: IAppState) => state.locale);
  const dateFormat = useSelector((state: IAppState) => state.defaultDateFormat);

  const columnConfig: Array<ColumnConfig<HistoricalRedemption>> = [
    { id: 1, title: getResource(resources, 'VIEW_DAYS_TAB_COLUMN_REDEMPTION_ID'), onSort: sortByProp('redemptionID', false) },
    { id: 2, title: getResource(resources, 'VIEW_DAYS_TAB_COLUMN_TYPE'), onSort: sortByProp('redemptionTypeId', true) },
    {
      id: 3, 
      title:                   getResource(resources, 'VIEW_DAYS_TAB_COLUMN_DATE'),
      onSort:                  sortByDateProp('redeemDate'),
      defaultSortingColumn:    true,
      defaultSortingDirection: 'desc',
    },
    { id: 4, title: getResource(resources, 'VIEW_DAYS_TAB_COLUMN_RESORT'), onSort: sortByProp('redeemResort', true) },
    { id: 5, title: getResource(resources, 'VIEW_DAYS_TAB_COLUMN_MODIFIED_BY'), onSort: sortByProp('modifiedById', true) },
    { id: 6, title: getResource(resources, 'VIEW_DAYS_TAB_COLUMN_DAYS_USED'), onSort: sortByProp('redeemQuantity', false) },
    { id: 7, title: getResource(resources, 'VIEW_DAYS_TAB_COLUMN_NOTES'), onSort: sortByProp('redemptionExternalCode', true) },
  
  ];

  const getResortHistoryRef = React.useRef(props.getResortHistory);
  const allResortsRef = React.useRef(props.allResorts);
  useEffect(() => {
    getResortHistoryRef.current = props.getResortHistory;
    allResortsRef.current = props.allResorts;
  });

  useEffect(() => {
    const passInfo = props.preferredPass;
    const startingPassId = passInfo?.entitlementId;
    const homeResortAbbrev = props.homeResort?.resortAbbreviation || '';

    setSelectedPassId(startingPassId);
    setSelectedResort(homeResortAbbrev);

    getResortHistoryRef.current?.(
      props.guest.masterGuestId,
      startingPassId!,
      homeResortAbbrev,
      getResortName(homeResortAbbrev, allResortsRef.current!)
    );
  }, [props.preferredPass, props.homeResort?.resortAbbreviation, props.guest.masterGuestId]);

  useEffect(() => {
    const shouldUpdate = (
      (selectedPassId !== props.passEntitlementId && props.passEntitlementId! >= 0) ||
      (selectedResort !== props.homeResort?.resortAbbreviation && !isNilOrEmpty(props.passEntitlementId))
    );

    if (shouldUpdate && selectedResort && selectedPassId) {
      const resortName = getResortName(selectedResort, allResortsRef.current || ImmutableMap.empty());
      getResortHistoryRef.current?.(
        props.guest.masterGuestId,
        selectedPassId,
        selectedResort,
        resortName
      );
    }
  }, [selectedResort, selectedPassId, props.passEntitlementId, props.homeResort?.resortAbbreviation, props.guest.masterGuestId])


  const redeemEntitlement = () => {
    const redemption: Redemption = {
      guestId:                props.guest.masterGuestId,
      entitlementId:          selectedPassId || 0,
      redemptionDate:         new Date(selectedRedemption?.redeemDate || ''),
      redemptionQuantity:     -1,
      redemptionIdReference:  `${selectedRedemption?.redemptionID!}`,
      redemptionExternalCode: dayCorrectionNotes,
      modifiedById:           `${selectedRedemption?.modifiedById!}`,
    };

    props.redeemEntitlement(redemption);
  };

  const mapResortsForDropdown = (): ResortDropdownItem[] => (
    props.allResorts ? props.allResorts.getValues() : []
  ).map((resort: Resort) => {
    const guestHistory = props.resortHistories!.get(resort.resortAbbreviation);

    return {
      name:         resort.resortName,
      resortAbbrev: resort.resortAbbreviation,
      totalDays:    R.path([ 'maxAllowed' ], guestHistory),
      usedDays:     R.path([ 'used' ], guestHistory),
    };
  });

  const onPassChange = (entitlementId: number) => {
    const homeResort = props.homeResort?.resortAbbreviation || '-1';

    setSelectedPassId(entitlementId);
    setSelectedResort(homeResort);

    if (isNilOrEmpty(homeResort) || homeResort === '-1') {
      return;
    }

    props.getResortHistory!(
      props.guest.masterGuestId,
      entitlementId,
      homeResort,
      getResortName(homeResort, props.allResorts!),
    );
  };

  const onResortChange = (resortAbbrev: string) => {
    setSelectedResort(resortAbbrev);

    if (isNilOrEmpty(resortAbbrev)) {
      return;
    }

    props.getResortHistory!(
      props.guest.masterGuestId,
      selectedPassId!,
      resortAbbrev,
      getResortName(resortAbbrev, props.allResorts!),
    );
  }

  const saveDayCorrection = () => {
    redeemEntitlement();
    setIsDayCorrectionModalOpen(false);
  };

  const renderDayCorrectionModal = () =>
    <ModalView onDismiss={ () => setIsDayCorrectionModalOpen(false)}>
      <ModalTitleHeader resources={resources} heading={getResource(resources, 'VIEW_DAYS_TAB_MODAL_HEADER')} icon={dtw} onDismiss={()=>setIsDayCorrectionModalOpen(false)}/>
      <ModalContent>
        <DayCorrectionForm
          redemptionId={`${selectedRedemption?.redemptionID}`}
          onSave={saveDayCorrection}
          note={dayCorrectionNotes}
          onNoteChange={(note) => setDayCorrectionNotes(note)}
        />
      </ModalContent>
    </ModalView>;

  const getDaysPerId =
    (redemption: HistoricalRedemption, fullHistory: HistoricalRedemption[]): number => {
      if (redemption.redemptionSplitFactor === 2) {
        const positiveRedemptions = redemption.redemptionID as string
        const ids = positiveRedemptions.split('/').map(id => parseInt(id));

        const filter = (it) =>
          ids.includes(it.redemptionID) || ids.includes(it.redemptionIdReference);

        return fullHistory
          .filter(it => filter(it))
          .map(it => it.redeemQuantity)
          .reduce((sum,it) => sum + it, 0);
      }

      const filter = (it) =>
        redemption.redemptionID === it.redemptionID ||
        redemption.redemptionID === it.redemptionIdReference;

      return fullHistory
        .filter(it => filter(it))
        .map(it => it.redeemQuantity)
        .reduce((sum,it) => sum + it, 0);
    };

  const canUserRedeem = (redemption: HistoricalRedemption): boolean =>{
    return getResortName(selectedResort, props.allResorts!) === redemption.redeemResort;
  }

  const openDayCorrectionModal = (selectedRedemption: HistoricalRedemption) => () => {
    setIsDayCorrectionModalOpen(true);
    setSelectedRedemption(selectedRedemption);
  };

  const renderDayCorrectionButton = (redemption: HistoricalRedemption) =>
    <img
      css={css`cursor: pointer;`}
      src={xCircle}
      title={getResource(resources, 'VIEW_DAYS_CORRECTION_BUTTON_TITLE')}
      alt="xCircle"
      onClick={openDayCorrectionModal(redemption)}
    />;

  const renderDayCorrectionCellContents = (redemption: HistoricalRedemption, fullHistory: HistoricalRedemption[]) => {
    if (redemption.redeemQuantity < 1) {
      return "";
    }

    const allowRedemption = getDaysPerId(redemption, fullHistory) > 0;

    if (!canUserRedeem(redemption)){
      return "";
    }

    return allowRedemption && props.allowCorrectiveRedemption ?  renderDayCorrectionButton(redemption) : "";
  };


  const renderRow = (
    redemption: HistoricalRedemption,
    fullHistory: HistoricalRedemption[],
    typeList: RedemptionType[] | undefined
  ): React.ReactNode =>(
    <tr key={redemption.redemptionID}>
      <td css={css`width: 150px;`}>{redemption.redemptionID}</td>
      <td>{ typeList ? getRedemptionType(typeList, redemption.redemptionTypeId) : redemption.redemptionTypeId }</td>
      <td css={css`width: 130px;`}>{formatDate(redemption.redeemDate, dateFormat)}</td>
      <td>{redemption.redeemResort} - {redemption.redeemLocation}</td>
      <td>{redemption.modifiedById}</td>
      <td css={css`width: 60px; text-align: right;`}>{redemption.redeemQuantity}</td>
      <td css={css`max-width: 600px; word-wrap: break-word; hyphens: auto;`}>{redemption.redemptionExternalCode}</td>
      {<td>{renderDayCorrectionCellContents(redemption, fullHistory)}</td>}
    </tr>
  );

  const renderRowWithTypes = (redemption: HistoricalRedemption) => {
    const entitlement: Entitlement = props.resortHistories!.get(selectedResort);
    // const filteredEntitlements = this.props.resortHistories!.getValues().filter((entitlement: Entitlement) => entitlee);
    const fullHistory: HistoricalRedemption[] = entitlement?.redemptionHistory ?? [];
    return renderRow(redemption, fullHistory, props.redemptionTypes);
  };

  const renderResortDetails = (history: Entitlement) => {
    if (history.resortAbbrev === ALL_RESORTS) {
      const stats = getAggregateStats(history);

      return (
        <div css={ViewDaysStats}>
          <StatSummary label={getResource(resources, 'VIEW_DAYS_STATS_TOTAL_DAYS')} value={stats.totalDays}/>
          <StatSummary label={getResource(resources, 'VIEW_DAYS_STATS_RESORTS')} value={stats.resorts}/>
          <StatSummary label={getResource(resources, 'VIEW_DAYS_STATS_MOST_VISITED', stats.mostVisited.name)} value={stats.mostVisited.stays}/>
        </div>
      );
    }

    const noDaysMsg = R.path([ 'resortHistory', 'maxAllowed' ], props)
    || R.path([ 'resortHistory', 'used' ], props)
    || R.path([ 'resortHistory', 'available' ], props)
      ? '0'
      : getResource(resources, 'VIEW_DAYS_TAB_NOT_APPLICABLE');

    return (
      <RedemptionDetails
        totalDays={getFriendlyDays(resources, R.path([ 'maxAllowed' ], history), noDaysMsg)}
        daysUsed={getFriendlyDays(resources, R.path([ 'used' ], history), noDaysMsg)}
        remainingDaysAvailable={getFriendlyDays(resources, R.path([ 'available' ], history), noDaysMsg)}
        disableTooltip={true}
      />
    );
  };

  const mapPassesFor = (entitlements: EntitlementRedemptionHistory[]): QuickEntitlement[] => R.sortBy(
    entitlement => entitlement.name,
    entitlements.map(pass => ({
      masterGuestId: pass.masterGuestId,
      entitlementId: pass.entitlementId,
      entitlementTypeId: pass.entitlementTypeId,
      entitlementCategoryId: pass.entitlementCatergoryId,
      // entitlementFulfillmentSeasonAttributes: [],
      entitlementStatusId: pass.entitlementStatusId,
      name: `${toTwoDigitYearRanges(pass.sellingSeasonDescription)} ${pass.entitlementDescription}`,
      expirationDate: '',
      effectiveDate: '',
      externalId: '',
      entitlementTypeAttributes: [],
      prepopulateGuest: false,
      isLocationRestricted: undefined,
    })))
    .reverse();

  const history = props.resortHistories!.get(selectedResort);
  const uncombinedSplitHistory: HistoricalRedemption[] = R.path([ 'redemptionHistory' ], history) || [];

  return (
    <div css={COLUMN_SPACING}>
      {/* Guest Name */}
      <LabeledData label={getResource(resources, 'VIEW_DAYS_GUEST_LABEL')} data={fullName(props.guest)}/>

      <PassResortSelector passes={mapPassesFor(props.redemptions || [])}
                          allEntitlementTypes={props.allEntitlementTypes}
                          resorts={mapResortsForDropdown()}
                          selectedPass={selectedPassId || 0}
                          selectedResort={selectedResort}
                          isLoadingResorts={!!props.isLoadingResorts}
                          onPassChange={onPassChange}
                          onResortChange={onResortChange}
                          resources={resources}/>

      {/* Resorts For Entitlement Load Failure */}
      <ErrorView error={
        !props.isLoadingResorts && R.path([ 'errorPassResorts', 'errorMsg' ], props)
          ? getResource(resources, 'VIEW_DAYS_FAILED_FETCH', apostrophize(fullName(props.guest)))
          : undefined
      }/>

      {/* History for Resort Load Failure */}
      <ErrorView error={
        !props.isLoadingResortHistory!(selectedResort!)
        && R.path([ 'errorResortHistory', 'errorMsg' ], props)
          ? getResource(resources, 'VIEW_DAYS_FAILED_HISTORY', apostrophize(fullName(props.guest)))
          : undefined
      }/>

      {isDayCorrectionModalOpen && renderDayCorrectionModal()}

      {history && renderResortDetails(history)}

      {/* Skier Day Summary Table / Id, Type, Date, Quantity */}
      <Table columns={columnConfig}
             data={combineSplitUsageDays(uncombinedSplitHistory) || []}
             isLoading={props.isLoadingResortHistory!(selectedResort)}
             noDataMessage={getResource(resources, 'VIEW_DAYS_NO_DATA')}
             renderRow={renderRowWithTypes}/>
    </div>
  );
}
